/**
 * P5 Pacote B — AML / PEP / EDD command page.
 * Route: /admin/risk/aml
 *
 * Single page, three tabs:
 *   - AML Alerts (with rule engine + sweep trigger)
 *   - PEP Screenings
 *   - EDD Cases
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  ShieldAlert, Users, FileSearch, Sparkles, RefreshCw, PlayCircle, Loader2,
  CheckCircle2, AlertTriangle, Flag, Clock, Mail, Activity, ChevronRight, X,
  ListChecks, Plus, FileText,
} from 'lucide-react';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { Badge } from '../../../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../../components/ui/select';
import { useToast } from '../../../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SEVERITY_BADGE = {
  low:      'bg-slate-200 text-slate-700',
  medium:   'bg-amber-200 text-amber-900',
  high:     'bg-orange-500 text-white',
  critical: 'bg-rose-600 text-white',
};

const STATUS_BADGE = {
  open:              'bg-amber-100 text-amber-800',
  reviewed:          'bg-sky-100 text-sky-800',
  cleared:           'bg-emerald-100 text-emerald-800',
  escalated_to_edd:  'bg-rose-100 text-rose-800',
};

const EDD_STATE_BADGE = {
  required:          'bg-amber-100 text-amber-800',
  in_progress:       'bg-sky-100 text-sky-800',
  pending_approval:  'bg-violet-100 text-violet-800',
  approved:          'bg-emerald-100 text-emerald-800',
  rejected:          'bg-rose-100 text-rose-800',
  expired:           'bg-slate-300 text-slate-800',
};

const TABS = [
  { id: 'alerts', label: 'AML Alerts', icon: ShieldAlert },
  { id: 'pep',    label: 'PEP Screenings', icon: Users },
  { id: 'edd',    label: 'EDD Cases', icon: FileSearch },
];


const AMLCenter = () => {
  const { toast } = useToast();
  const auth = useMemo(() => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` },
  }), []);

  const [tab, setTab] = useState('alerts');
  const [kpis, setKpis] = useState(null);

  // AML alerts
  const [alerts, setAlerts] = useState([]);
  const [alertsLoading, setAlertsLoading] = useState(false);
  const [sweepRunning, setSweepRunning] = useState(false);

  // PEP
  const [screenings, setScreenings] = useState([]);
  const [pepLoading, setPepLoading] = useState(false);
  const [pepActing, setPepActing] = useState(false);

  // EDD
  const [cases, setCases] = useState([]);
  const [eddLoading, setEddLoading] = useState(false);
  const [activeCase, setActiveCase] = useState(null);
  const [decisionOpen, setDecisionOpen] = useState(false);
  const [decisionType, setDecisionType] = useState('approved');
  const [decisionNote, setDecisionNote] = useState('');

  // ============================================================ loaders
  const loadKpis = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/api/aml/kpis`, auth);
      setKpis(r.data);
    } catch { /* graceful */ }
  }, [auth]);

  const loadAlerts = useCallback(async () => {
    setAlertsLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/aml/alerts?limit=200`, auth);
      setAlerts(r.data || []);
    } catch (e) {
      toast({ title: 'Could not load alerts', description: e?.message, variant: 'destructive' });
    } finally { setAlertsLoading(false); }
  }, [auth, toast]);

  const loadScreenings = useCallback(async () => {
    setPepLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/pep/screenings?limit=200`, auth);
      setScreenings(r.data || []);
    } catch (e) {
      toast({ title: 'Could not load PEP screenings', description: e?.message, variant: 'destructive' });
    } finally { setPepLoading(false); }
  }, [auth, toast]);

  const loadCases = useCallback(async () => {
    setEddLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/edd/cases?limit=200`, auth);
      setCases(r.data || []);
    } catch (e) {
      toast({ title: 'Could not load EDD cases', description: e?.message, variant: 'destructive' });
    } finally { setEddLoading(false); }
  }, [auth, toast]);

  useEffect(() => {
    loadKpis();
    if (tab === 'alerts') loadAlerts();
    if (tab === 'pep') loadScreenings();
    if (tab === 'edd') loadCases();
  }, [tab, loadKpis, loadAlerts, loadScreenings, loadCases]);

  // ============================================================ actions
  const runSweep = useCallback(async () => {
    setSweepRunning(true);
    try {
      const r = await axios.post(`${API_URL}/api/aml/sweep/run?window_days=60`, {}, auth);
      const totalNew = Object.values(r.data.alerts_by_rule || {}).reduce((s, n) => s + n, 0);
      toast({
        title: 'AML sweep complete',
        description: `Scanned ${r.data.transactions_scanned} tx · ${totalNew} new alert(s).`,
      });
      await loadAlerts(); await loadKpis();
    } catch (e) {
      toast({ title: 'Sweep failed', description: e?.message, variant: 'destructive' });
    } finally { setSweepRunning(false); }
  }, [auth, toast, loadAlerts, loadKpis]);

  const reviewAlert = useCallback(async (alert, status) => {
    try {
      await axios.post(`${API_URL}/api/aml/alerts/${alert.id}/review`, {
        status, note: status === 'escalated_to_edd' ? 'Escalated for EDD review' : null,
      }, auth);
      toast({ title: `Alert ${status.replace('_', ' ')}` });
      await loadAlerts(); await loadKpis();
    } catch (e) {
      toast({ title: 'Review failed', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    }
  }, [auth, toast, loadAlerts, loadKpis]);

  const refreshPepFeed = useCallback(async () => {
    setPepActing(true);
    try {
      const r = await axios.post(`${API_URL}/api/pep/refresh`, {}, auth);
      toast({
        title: `PEP feed ${r.data?.status || 'updated'}`,
        description: `Upserted ${r.data?.upserted_total ?? 0} entries.`,
      });
    } catch (e) {
      toast({ title: 'PEP refresh failed', description: e?.message, variant: 'destructive' });
    } finally { setPepActing(false); }
  }, [auth, toast]);

  const screenAll = useCallback(async () => {
    setPepActing(true);
    try {
      const r = await axios.post(`${API_URL}/api/pep/screen/all`, {}, auth);
      toast({
        title: 'Screening complete',
        description: `${r.data?.total_screened} screened · ${r.data?.matches} match(es).`,
      });
      await loadScreenings(); await loadKpis();
    } catch (e) {
      toast({ title: 'Bulk screening failed', description: e?.message, variant: 'destructive' });
    } finally { setPepActing(false); }
  }, [auth, toast, loadScreenings, loadKpis]);

  const openCase = useCallback(async (caseId) => {
    try {
      const r = await axios.get(`${API_URL}/api/edd/cases/${caseId}`, auth);
      setActiveCase(r.data);
    } catch (e) {
      toast({ title: 'Could not open case', variant: 'destructive' });
    }
  }, [auth, toast]);

  const toggleChecklist = useCallback(async (key, completed) => {
    if (!activeCase) return;
    try {
      const r = await axios.patch(
        `${API_URL}/api/edd/cases/${activeCase.id}/checklist`,
        { key, completed }, auth,
      );
      setActiveCase(r.data); await loadCases(); await loadKpis();
    } catch (e) {
      toast({ title: 'Checklist update failed', variant: 'destructive' });
    }
  }, [activeCase, auth, toast, loadCases, loadKpis]);

  const submitDecision = useCallback(async () => {
    if (!activeCase) return;
    try {
      const r = await axios.post(
        `${API_URL}/api/edd/cases/${activeCase.id}/decision`,
        { decision: decisionType, note: decisionNote || null }, auth,
      );
      toast({ title: `Case ${decisionType}` });
      setActiveCase(r.data); setDecisionOpen(false); setDecisionNote('');
      await loadCases(); await loadKpis();
    } catch (e) {
      toast({ title: 'Decision failed', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    }
  }, [activeCase, decisionType, decisionNote, auth, toast, loadCases, loadKpis]);

  // ============================================================ render
  return (
    <div data-testid="aml-center-page">
      {/* Header */}
      <div className="mb-6 flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-rose-600 to-orange-500 flex items-center justify-center shadow-lg">
          <ShieldAlert className="w-5 h-5 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-[#2B2D42]">AML / PEP / EDD</h1>
          <p className="text-sm text-[#6C757D]">Transaction monitoring, politically exposed persons screening and enhanced due diligence.</p>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-5" data-testid="aml-kpis">
        <Kpi label="Open alerts" value={kpis?.alerts_open ?? '—'} icon={ShieldAlert} accent="amber" />
        <Kpi label="High / Critical" value={kpis?.alerts_high_or_critical ?? '—'} icon={Flag} accent="rose" />
        <Kpi label="New (24h)" value={kpis?.alerts_new_24h ?? '—'} icon={Activity} accent="sky" />
        <Kpi label="PEP matches" value={kpis?.pep_matches ?? '—'} icon={Users} accent="violet" />
        <Kpi label="EDD active" value={(kpis?.edd_required ?? 0) + (kpis?.edd_in_progress ?? 0) + (kpis?.edd_pending_approval ?? 0)} icon={FileSearch} accent="orange" />
        <Kpi label="EDD approved" value={kpis?.edd_approved ?? '—'} icon={CheckCircle2} accent="emerald" />
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-4 border-b border-slate-200" data-testid="aml-tabs">
        {TABS.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`aml-tab-${t.id}`}
            className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px flex items-center gap-1.5 transition-colors ${
              tab === t.id
                ? 'border-orange-500 text-orange-600'
                : 'border-transparent text-slate-500 hover:text-slate-800'
            }`}>
            <t.icon className="w-4 h-4" />{t.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {tab === 'alerts' && (
        <div data-testid="aml-alerts-tab">
          <div className="mb-3 flex justify-end">
            <Button onClick={runSweep} disabled={sweepRunning} data-testid="aml-run-sweep-btn"
              className="bg-orange-600 hover:bg-orange-700 text-white">
              {sweepRunning
                ? <><Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />Running…</>
                : <><PlayCircle className="w-3.5 h-3.5 mr-1.5" />Run AML sweep</>}
            </Button>
          </div>
          <Card><CardContent className="p-0">
            {alertsLoading ? (
              <div className="p-8 text-center text-slate-500"><Loader2 className="w-4 h-4 animate-spin inline mr-2"/>Loading…</div>
            ) : alerts.length === 0 ? (
              <div className="p-12 text-center text-slate-500" data-testid="aml-alerts-empty">
                <ShieldAlert className="w-10 h-10 mx-auto mb-3 text-slate-300"/>
                No AML alerts. Run a sweep to scan recent transactions against the active rules.
              </div>
            ) : (
              <ul className="divide-y divide-slate-100" data-testid="aml-alerts-list">
                {alerts.map((a) => (
                  <li key={a.id} className="px-5 py-4 flex items-start gap-4" data-testid={`aml-alert-${a.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <span className="font-semibold text-[#2B2D42]">{a.rule_name || a.rule_kind}</span>
                        <Badge className={`${SEVERITY_BADGE[a.severity] || ''} text-[10px]`}>{(a.severity || '').toUpperCase()}</Badge>
                        <Badge className={`${STATUS_BADGE[a.status] || ''} text-[10px] uppercase`}>{(a.status || '').replace('_', ' ')}</Badge>
                      </div>
                      <p className="text-sm text-[#475569]">{a.description}</p>
                      <div className="text-[11px] text-[#6C757D] mt-1 flex flex-wrap gap-x-3">
                        {a.counterparty_name && <span>Counterparty: {a.counterparty_name}</span>}
                        {a.amount > 0 && <span>{a.amount.toLocaleString()} {a.currency}</span>}
                        <span><Clock className="w-3 h-3 inline mr-1"/>{(a.created_at || '').slice(0, 19).replace('T', ' ')}</span>
                      </div>
                    </div>
                    {a.status === 'open' && (
                      <div className="flex flex-col gap-1.5">
                        <Button size="sm" variant="outline" onClick={() => reviewAlert(a, 'cleared')}
                          data-testid={`aml-clear-${a.id}`}>Clear</Button>
                        <Button size="sm" onClick={() => reviewAlert(a, 'escalated_to_edd')}
                          data-testid={`aml-escalate-${a.id}`}
                          className="bg-rose-600 hover:bg-rose-700 text-white">Escalate to EDD</Button>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </CardContent></Card>
        </div>
      )}

      {tab === 'pep' && (
        <div data-testid="aml-pep-tab">
          <div className="mb-3 flex justify-end gap-2">
            <Button onClick={refreshPepFeed} disabled={pepActing} data-testid="pep-refresh-btn"
              variant="outline">
              <RefreshCw className="w-3.5 h-3.5 mr-1.5"/>Refresh PEP feed
            </Button>
            <Button onClick={screenAll} disabled={pepActing} data-testid="pep-screen-all-btn"
              className="bg-violet-600 hover:bg-violet-700 text-white">
              {pepActing ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5"/> : <Users className="w-3.5 h-3.5 mr-1.5"/>}
              Screen all counterparties
            </Button>
          </div>
          <Card><CardContent className="p-0">
            {pepLoading ? (
              <div className="p-8 text-center text-slate-500"><Loader2 className="w-4 h-4 animate-spin inline mr-2"/>Loading…</div>
            ) : screenings.length === 0 ? (
              <div className="p-12 text-center text-slate-500" data-testid="pep-screenings-empty">
                <Users className="w-10 h-10 mx-auto mb-3 text-slate-300"/>
                No PEP screenings yet. Refresh the PEP feed and run "Screen all counterparties".
              </div>
            ) : (
              <ul className="divide-y divide-slate-100" data-testid="pep-screenings-list">
                {screenings.map((s) => (
                  <li key={s.id} className="px-5 py-4 flex items-start gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-semibold text-[#2B2D42]">{s.counterparty_name}</span>
                        {s.matched
                          ? <Badge className="bg-rose-600 text-white text-[10px]">PEP MATCH</Badge>
                          : <Badge className="bg-emerald-100 text-emerald-800 text-[10px]">CLEAR</Badge>}
                        {s.match_tier && <Badge className="bg-violet-100 text-violet-800 text-[10px]">{s.match_tier.replace('_', ' ').toUpperCase()}</Badge>}
                      </div>
                      <div className="text-[11px] text-[#6C757D] flex flex-wrap gap-x-3">
                        <span><Clock className="w-3 h-3 inline mr-1"/>{(s.screened_at || '').slice(0, 19).replace('T', ' ')}</span>
                        {(s.matched_entries || []).length > 0 && (
                          <span>Hits: {s.matched_entries.slice(0, 2).map(e => e.name).join(', ')}</span>
                        )}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </CardContent></Card>
        </div>
      )}

      {tab === 'edd' && (
        <div data-testid="aml-edd-tab">
          <Card><CardContent className="p-0">
            {eddLoading ? (
              <div className="p-8 text-center text-slate-500"><Loader2 className="w-4 h-4 animate-spin inline mr-2"/>Loading…</div>
            ) : cases.length === 0 ? (
              <div className="p-12 text-center text-slate-500" data-testid="edd-cases-empty">
                <FileSearch className="w-10 h-10 mx-auto mb-3 text-slate-300"/>
                No EDD cases. They are auto-created when an AML alert is escalated or a PEP match is found.
              </div>
            ) : (
              <ul className="divide-y divide-slate-100" data-testid="edd-cases-list">
                {cases.map((c) => (
                  <li key={c.id} onClick={() => openCase(c.id)}
                    data-testid={`edd-case-${c.id}`}
                    className="px-5 py-4 flex items-center gap-4 cursor-pointer hover:bg-slate-50">
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <span className="font-semibold text-[#2B2D42]">{c.counterparty_name}</span>
                        <span className="text-[10px] font-mono text-[#6C757D]">{c.case_number}</span>
                        <Badge className={`${EDD_STATE_BADGE[c.state] || ''} text-[10px] uppercase`}>{(c.state || '').replace('_', ' ')}</Badge>
                        <Badge className="bg-slate-100 text-slate-700 text-[10px]">{(c.reason || '').toUpperCase()}</Badge>
                      </div>
                      <div className="text-[11px] text-[#6C757D] flex flex-wrap gap-x-3">
                        <span>Triggered: {(c.triggered_at || '').slice(0, 10)}</span>
                        {c.due_at && <span>Due: {(c.due_at || '').slice(0, 10)}</span>}
                        {c.assignee_email && <span><Mail className="w-3 h-3 inline mr-1"/>{c.assignee_email}</span>}
                        <span>Checklist: {(c.checklist || []).filter(x => x.completed).length}/{(c.checklist || []).length}</span>
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-slate-400"/>
                  </li>
                ))}
              </ul>
            )}
          </CardContent></Card>
        </div>
      )}

      {/* EDD case drawer */}
      <Dialog open={!!activeCase} onOpenChange={() => setActiveCase(null)}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="edd-case-drawer">
          {activeCase && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 flex-wrap">
                  {activeCase.counterparty_name}
                  <span className="text-xs font-mono text-slate-400">{activeCase.case_number}</span>
                  <Badge className={`${EDD_STATE_BADGE[activeCase.state]} text-[10px] uppercase`}>{(activeCase.state || '').replace('_', ' ')}</Badge>
                </DialogTitle>
                <DialogDescription className="sr-only">
                  Enhanced Due Diligence case detail drawer with checklist, activity log and approval actions.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-5">
                <div className="text-xs text-slate-500 grid grid-cols-2 gap-2">
                  <div><b>Reason:</b> {(activeCase.reason || '').toUpperCase()}</div>
                  <div><b>Triggered by:</b> {activeCase.triggered_by}</div>
                  <div><b>Due by:</b> {(activeCase.due_at || '').slice(0, 10)}</div>
                  <div><b>Assignee:</b> {activeCase.assignee_email || '—'}</div>
                </div>

                <div>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-[#6C757D] mb-2">Checklist</div>
                  <ul className="space-y-1.5" data-testid="edd-checklist">
                    {(activeCase.checklist || []).map((it) => (
                      <li key={it.key} className="flex items-start gap-3 p-2.5 rounded-lg bg-slate-50 border border-slate-100">
                        <button onClick={() => toggleChecklist(it.key, !it.completed)}
                          data-testid={`edd-check-${it.key}`}
                          className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                            it.completed
                              ? 'bg-emerald-500 border-emerald-500'
                              : 'border-slate-300 hover:border-emerald-400'
                          }`}>
                          {it.completed && <CheckCircle2 className="w-3 h-3 text-white"/>}
                        </button>
                        <div className="flex-1">
                          <div className={`text-sm ${it.completed ? 'text-slate-500 line-through' : 'text-[#2B2D42]'}`}>{it.label}</div>
                          {it.evidence && <div className="text-[11px] text-slate-500 mt-0.5">Evidence: {it.evidence}</div>}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>

                {(activeCase.notes || []).length > 0 && (
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.18em] text-[#6C757D] mb-2">Activity</div>
                    <ul className="space-y-1 text-xs">
                      {activeCase.notes.map((n, i) => (
                        <li key={i} className="text-slate-600">
                          <span className="font-mono text-slate-400">{(n.at || '').slice(0, 19).replace('T', ' ')}</span>
                          {' · '}<b>{n.by}</b>: {n.note}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
              <DialogFooter className="flex flex-wrap gap-2">
                {(activeCase.state === 'pending_approval' || activeCase.state === 'in_progress') && (
                  <>
                    <Button variant="outline" onClick={() => { setDecisionType('rejected'); setDecisionOpen(true); }}
                      data-testid="edd-reject-btn"
                      className="text-rose-600 border-rose-200 hover:bg-rose-50">Reject</Button>
                    <Button onClick={() => { setDecisionType('approved'); setDecisionOpen(true); }}
                      data-testid="edd-approve-btn"
                      className="bg-emerald-600 hover:bg-emerald-700 text-white">Approve</Button>
                  </>
                )}
                {activeCase.decision && (
                  <span className="text-xs text-slate-500">
                    Decision: <b>{activeCase.decision}</b> — {(activeCase.decision_at || '').slice(0, 10)}
                  </span>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Decision dialog */}
      <Dialog open={decisionOpen} onOpenChange={setDecisionOpen}>
        <DialogContent data-testid="edd-decision-dialog">
          <DialogHeader>
            <DialogTitle>{decisionType === 'approved' ? 'Approve EDD case' : 'Reject EDD case'}</DialogTitle>
            <DialogDescription className="sr-only">
              Capture an optional rationale for the EDD case decision. The note is recorded in the audit trail.
            </DialogDescription>
          </DialogHeader>
          <Textarea value={decisionNote} onChange={(e) => setDecisionNote(e.target.value)} rows={4}
            placeholder="Optional decision rationale (recorded in audit trail)"
            data-testid="edd-decision-note" />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDecisionOpen(false)}>Cancel</Button>
            <Button onClick={submitDecision} data-testid="edd-decision-confirm-btn"
              className={decisionType === 'approved' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-rose-600 hover:bg-rose-700'}>
              {decisionType === 'approved' ? 'Approve' : 'Reject'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};


/* ------------------------------------------------------------ subcomponents */
const Kpi = ({ label, value, icon: Icon, accent }) => {
  const map = {
    amber:   { bg: 'bg-amber-50',   border: 'border-amber-200',   text: 'text-amber-700' },
    rose:    { bg: 'bg-rose-50',    border: 'border-rose-200',    text: 'text-rose-700' },
    sky:     { bg: 'bg-sky-50',     border: 'border-sky-200',     text: 'text-sky-700' },
    violet:  { bg: 'bg-violet-50',  border: 'border-violet-200',  text: 'text-violet-700' },
    orange:  { bg: 'bg-orange-50',  border: 'border-orange-200',  text: 'text-orange-700' },
    emerald: { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700' },
  }[accent] || { bg: 'bg-slate-50', border: 'border-slate-200', text: 'text-slate-700' };
  return (
    <div className={`p-3 rounded-xl border ${map.bg} ${map.border}`}>
      <div className="flex items-center justify-between mb-1">
        <div className="text-[10px] uppercase tracking-[0.16em] text-[#6C757D]">{label}</div>
        <Icon className={`w-3.5 h-3.5 ${map.text}`} />
      </div>
      <div className="text-2xl font-bold text-[#2B2D42]">{value}</div>
    </div>
  );
};

export default AMLCenter;
